import styles from './styles.scss';
import { CustomerOrder } from 'models/CustomerOrder';
import { CustomerPageGroupType } from 'models/CustomerPageGroupType';
import DeliveryGroup from './DeliveryGroup';
import Address from './Address';
import Price from 'components/Shared/Price';
import { rm } from 'utils';
import cn from 'classnames';
import { Button } from 'components/Button';
import { DiscountSpecification, DisplayMode } from './OrderLine';
import { useToggle } from 'hooks';
import { HelpModal } from '../HelpModal/HelpModal';
import { SupportProductModel } from 'models/SupportProductModel';

interface OwnProps {
  className?: string;
  order: CustomerOrder;
  displayMode: DisplayMode;
  returnProductChanged?: (product: SupportProductModel) => void;
}

export default function ExtendedInfo({
  className,
  order,
  displayMode,
  returnProductChanged
}: OwnProps) {
  const [showHelpModal, toggleHelpModal] = useToggle(false);

  return (
    <div className={cn(styles.extendedInfo, className)}>
      <HelpModal show={showHelpModal} toggle={toggleHelpModal} order={order} />
      {order.groups &&
        order.groups
          .filter(g => g.type === CustomerPageGroupType.Normal)
          .map(g => (
            <DeliveryGroup
              key={g.deliveryNum}
              group={g}
              displayMode={displayMode}
              returnProductChanged={returnProductChanged}
            />
          ))}
      {displayMode === DisplayMode.Normal && (
        <div className={styles.orderMeta}>
          <div className={styles.buttons}>
            <Button
              className={styles.apperance}
              appearance="tertiary"
              border="thin"
              invert
              onClick={toggleHelpModal}
            >
              {rm('customerpage_order_button_help')}
            </Button>
            <Button
              className={styles.apperance}
              appearance="tertiary"
              border="thin"
              invert
              href={order.receiptUrl}
              target="_blank"
            >
              {rm('customerpage_order_button_receipt')}
            </Button>
          </div>
          {order.deliveryAddress && (
            <Address
              className={styles.deliveryaddress}
              address={order.deliveryAddress}
              header={rm('accountreceipt_deliveryaddress')}
            />
          )}
          {order.invoiceAddress && (
            <Address
              className={styles.invoiceaddress}
              address={order.invoiceAddress}
              header={rm('accountreceipt_invoiceaddress')}
            />
          )}
          <div className={styles.summary}>
            <div className={styles.blueBox}>
              <div className={styles.row}>
                <span className={styles.title}>{rm('customerpage_order_price')}</span>
                <Price className={styles.value} price={order.totalExcludingFees} />
              </div>
              {order.groups &&
                order.groups
                  .find(g => g.type === CustomerPageGroupType.FreightAndFees)
                  ?.lines.map(line => (
                    <div key={line.lineNumber} className={styles.row}>
                      <span className={styles.title}>
                        {line.isActive ? line.name : <del>{line.name}</del>}
                      </span>
                      <span className={styles.value}>
                        <DiscountSpecification line={line} />
                        <Price
                          price={line.linePrice}
                          appearance={line.isActive ? 'normal' : 'lineThrough'}
                        />
                      </span>
                    </div>
                  ))}
              <div className={cn(styles.row, styles.total)}>
                <span className={styles.title}>{rm('checkout_total')}</span>
                <Price className={styles.value} price={order.total} appearance="discount" />
              </div>
              {order.totalDiscountAmount > 0 && (
                <div className={cn(styles.row, styles.discount)}>
                  <span className={styles.title}>{rm('customerpage_order_discount_total')}</span>
                  <Price
                    className={styles.value}
                    price={order.totalDiscountAmount}
                    appearance="normal"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
